import {
  AlignmentType,
  BorderStyle,
  Document,
  ImageRun,
  Packer,
  PageOrientation,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";
import {
  DEFAULT_SCHOOL_ABREV,
  DEFAULT_SCHOOL_LOGO_URL,
  DEFAULT_SCHOOL_WEBSITE,
} from "../config/school";

const PURPLE = "43146D";
const DARK = "111827";
const BORDER = {
  style: BorderStyle.SINGLE,
  size: 1,
  color: "D5DCE8",
};

const safeText = (value) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const fileSafe = (value) =>
  safeText(value)
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const text = (value, options = {}) =>
  new TextRun({
    text: safeText(value),
    font: "Arial",
    color: options.color ?? DARK,
    bold: options.bold,
    size: options.size ?? 17,
  });

const paragraph = (children, options = {}) =>
  new Paragraph({
    children,
    alignment: options.alignment ?? AlignmentType.LEFT,
    spacing: options.spacing ?? { before: 0, after: 60 },
  });

const cell = (children, options = {}) =>
  new TableCell({
    children: Array.isArray(children) ? children : [children],
    verticalAlign: VerticalAlign.CENTER,
    shading: options.fill
      ? { type: ShadingType.CLEAR, color: "auto", fill: options.fill }
      : undefined,
    borders: {
      top: BORDER,
      bottom: BORDER,
      left: BORDER,
      right: BORDER,
    },
    width: options.width
      ? { size: options.width, type: WidthType.PERCENTAGE }
      : undefined,
    margins: {
      top: options.marginY ?? 45,
      bottom: options.marginY ?? 45,
      left: options.marginX ?? 60,
      right: options.marginX ?? 60,
    },
  });

const headerCell = (value, width) =>
  cell(paragraph([text(value, { bold: true, color: "FFFFFF", size: 17 })], {
    alignment: AlignmentType.CENTER,
    spacing: { before: 0, after: 0 },
  }), {
    fill: PURPLE,
    width,
  });

const valueCell = (value, width, alignment = AlignmentType.LEFT) =>
  cell(paragraph([text(value, { size: 17 })], {
    alignment,
    spacing: { before: 0, after: 0 },
  }), {
    width,
  });

const labelCell = (value, width) =>
  cell(paragraph([text(value, { bold: true, size: 17 })], {
    spacing: { before: 0, after: 0 },
  }), {
    width,
  });

const makeTable = (rows) =>
  new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.FIXED,
    rows,
  });

const getLogoUrl = ({ id, type, student }) => {
  if (id === "cry") return "https://smartedung.com/cry.png";
  if (id === DEFAULT_SCHOOL_ABREV) return DEFAULT_SCHOOL_LOGO_URL;
  if (type === "primary") {
    return "https://smartedung.com/SAC/secureadmin---/panel/img/PRIMARY.png";
  }
  if (student?.school) return "https://smartedung.com/sac/logo.png";
  return `${window.location.origin}/assets/smlogo.png`;
};

const fetchImage = async (url) => {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch {
    return null;
  }
};

const getAddress = ({ id, student }) => {
  if (id === "racy") return "Address Coming Soon";
  if (id === DEFAULT_SCHOOL_ABREV) {
    if (student?.school === "LIASU") return "101, Liasu Road, Egbe-Idimu, Lagos.";
    if (student?.school === "AKESAN") {
      return "18, Imam Saliu Street, Afolabi Bus-stop, Igando Rd, Lagos.";
    }
    return "22, Tolu Aluko Str., Off Liasu Rd, Council-Idimu, Lagos.";
  }
  return "No. 7, St Augustine's street, Idimu-Titun, Lagos State.";
};

const getSchoolName = ({ id, type }) => {
  if (id === "racy") return "RACY & STURDY SCHOOLS";
  if (id === DEFAULT_SCHOOL_ABREV) {
    return `EBUN HERITAGE ${
      type === "primary" || type === "nursery"
        ? "FOUNDATION SCHOOL"
        : "HIGH SCHOOL"
    }`;
  }
  return `ST AUGUSTINE'S ${
    type === "primary" || type === "nursery"
      ? "NURSERY/PRIMARY SCHOOL"
      : "COLLEGE"
  }`;
};

const getWebsite = (id) => {
  if (id === "cry") return "Coming Soon";
  if (id === DEFAULT_SCHOOL_ABREV) return DEFAULT_SCHOOL_WEBSITE;
  return "staugustinesschool.com";
};

const studentInfoTable = (student) =>
  makeTable([
    new TableRow({
      children: [
        labelCell("NAME", 12),
        valueCell(student?.name, 26),
        labelCell("STUDENT ID", 14),
        valueCell(student?.id, 18),
        labelCell("SEX", 10),
        valueCell(student?.sex, 20),
      ],
    }),
    new TableRow({
      children: [
        labelCell("TERM", 12),
        valueCell(student?.term, 26),
        labelCell("SESSION", 14),
        valueCell(student?.session, 18),
        labelCell("CLASS", 10),
        valueCell(student?.class, 20),
      ],
    }),
  ]);

const academicTable = ({
  results,
  isCumulative,
  resultType,
  id,
  calculateCumulativeAverage,
  calculateGradeRemark,
  schoolType,
}) => {
  const rows = [];

  if (isCumulative) {
    rows.push(
      new TableRow({
        tableHeader: true,
        children: [
          headerCell("Subject", 30),
          headerCell("First Term", 10),
          headerCell("Second Term", 10),
          headerCell("Third Term", 10),
          headerCell("Cumulative", 11),
          headerCell("Grade", 7),
          headerCell("Remark", 22),
        ],
      })
    );

    results.forEach((result) => {
      const cumulativeScore =
        result.cumulative_average ?? calculateCumulativeAverage(result);
      const gradeRemark = calculateGradeRemark(cumulativeScore, schoolType);

      rows.push(
        new TableRow({
          children: [
            valueCell(result.subject, 30),
            valueCell(result.first_term?.total, 10, AlignmentType.CENTER),
            valueCell(result.second_term?.total, 10, AlignmentType.CENTER),
            valueCell(result.third_term?.total, 10, AlignmentType.CENTER),
            valueCell(cumulativeScore, 11, AlignmentType.CENTER),
            valueCell(gradeRemark.grade, 7, AlignmentType.CENTER),
            valueCell(gradeRemark.remark, 22, AlignmentType.CENTER),
          ],
        })
      );
    });

    return makeTable(rows);
  }

  rows.push(
    new TableRow({
      tableHeader: true,
      children: [
        headerCell("Subject", 36),
        headerCell("Test", 14),
        ...(resultType !== "Test"
          ? [
              headerCell("Exam", 14),
              headerCell("Total", 14),
              headerCell("Grade", 10),
            ]
          : []),
        headerCell("Remark", resultType !== "Test" ? 12 : 50),
      ],
    })
  );

  results.forEach((result) => {
    rows.push(
      new TableRow({
        children: [
          valueCell(result.subject, 36),
          valueCell(
            id === DEFAULT_SCHOOL_ABREV && resultType === "Test" ? result.test_1 : result.test,
            14,
            AlignmentType.CENTER
          ),
          ...(resultType !== "Test"
            ? [
                valueCell(result.exam, 14, AlignmentType.CENTER),
                valueCell(result.total, 14, AlignmentType.CENTER),
                valueCell(result.grade, 10, AlignmentType.CENTER),
              ]
            : []),
          valueCell(result.remark, resultType !== "Test" ? 12 : 50, AlignmentType.CENTER),
        ],
      })
    );
  });

  return makeTable(rows);
};

const summaryTable = ({ meta, overGrade, overRemark, resultType }) =>
  makeTable([
    new TableRow({
      children: [
        valueCell(`Marks Obtainable: ${safeText(meta?.mark_obtain)}`, 20, AlignmentType.CENTER),
        valueCell(`Marks Obtained: ${safeText(meta?.total_mark)}`, 20, AlignmentType.CENTER),
        valueCell(`AVERAGE: ${safeText(meta?.average)}`, 20, AlignmentType.CENTER),
        valueCell(`Overall Grade: ${safeText(overGrade)}`, 20, AlignmentType.CENTER),
        valueCell(`Overall Remark: ${safeText(overRemark)}`, 20, AlignmentType.CENTER),
      ],
    }),
    ...(resultType !== "Test"
      ? [
          new TableRow({
            children: [
              cell(paragraph([
                text(
                  `Attendance: ${safeText(meta?.time_present)} / ${safeText(meta?.school_open)}`,
                  { size: 17, bold: true }
                ),
              ], {
                alignment: AlignmentType.CENTER,
                spacing: { before: 0, after: 0 },
              }), {
                width: 100,
              }),
            ],
          }),
        ]
      : []),
  ]);

const commentsTable = ({ meta, principalComment, resultType }) => {
  const rows = [];

  if (resultType !== "Test") {
    rows.push(
      new TableRow({
        children: [
          labelCell("Punctuality", 25),
          valueCell(meta?.punctuality, 25),
          labelCell("Neatness", 25),
          valueCell(meta?.neatness, 25),
        ],
      }),
      new TableRow({
        children: [
          labelCell("Honesty", 25),
          valueCell(meta?.honesty, 25),
          labelCell("Initiative", 25),
          valueCell(meta?.initiative, 25),
        ],
      }),
      new TableRow({
        children: [
          labelCell("Politeness", 25),
          valueCell(meta?.politeness, 25),
          labelCell("Attentive", 25),
          valueCell(meta?.attentive, 25),
        ],
      })
    );
  }

  rows.push(
    new TableRow({
      children: [
        labelCell("Teacher's Comment", 25),
        cell(paragraph([text(meta?.teach_com, { size: 17 })], {
          spacing: { before: 0, after: 0 },
        }), {
          width: 75,
        }),
      ],
    }),
    new TableRow({
      children: [
        labelCell("Principal's Comment", 25),
        cell(paragraph([text(principalComment, { size: 17 })], {
          spacing: { before: 0, after: 0 },
        }), {
          width: 75,
        }),
      ],
    })
  );

  return makeTable(rows);
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const exportResultDocx = async ({
  id,
  student,
  results,
  meta,
  resultType,
  type,
  overGrade,
  overRemark,
  principalComment,
  isCumulative,
  calculateCumulativeAverage,
  calculateGradeRemark,
}) => {
  const logoData = await fetchImage(getLogoUrl({ id, type, student }));

  const children = [
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: 18, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
              },
              children: [
                paragraph(
                  logoData
                    ? [
                        new ImageRun({
                          data: logoData,
                          transformation: { width: 52, height: 52 },
                        }),
                      ]
                    : [text("")],
                  { alignment: AlignmentType.CENTER }
                ),
              ],
            }),
            new TableCell({
              width: { size: 64, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
              },
              children: [
                paragraph([text(getSchoolName({ id, type }), {
                  bold: true,
                  color: PURPLE,
                  size: 25,
                })], {
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 0, after: 20 },
                }),
                paragraph([text(getAddress({ id, student }), { size: 18 })], {
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 0, after: 16 },
                }),
                paragraph([text(`Website: ${getWebsite(id)}`, { size: 18 })], {
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 0, after: 16 },
                }),
                paragraph([text(
                  type === "primary" || type === "nursery"
                    ? "Pupil's Report Sheet"
                    : "Student's Report Sheet",
                  { color: PURPLE, size: 20 }
                )], {
                  alignment: AlignmentType.CENTER,
                  spacing: { before: 0, after: 30 },
                }),
              ],
            }),
            new TableCell({
              width: { size: 18, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
              },
              children: [paragraph([text("")])],
            }),
          ],
        }),
      ],
    }),
    studentInfoTable(student),
    paragraph([text("Academic Performance", { color: PURPLE, size: 20 })], {
      alignment: AlignmentType.CENTER,
      spacing: { before: 50, after: 25 },
    }),
    academicTable({
      results,
      isCumulative,
      resultType,
      id,
      calculateCumulativeAverage,
      calculateGradeRemark,
      schoolType: student?.school_type,
    }),
    summaryTable({ meta, overGrade, overRemark, resultType }),
    paragraph([text("Behavioural / Teacher Comments", { color: PURPLE, size: 20 })], {
      alignment: AlignmentType.CENTER,
      spacing: { before: 30, after: 25 },
    }),
    commentsTable({ meta, principalComment, resultType }),
  ];

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: {
              orientation: PageOrientation.LANDSCAPE,
            },
            margin: {
              top: 230,
              right: 230,
              bottom: 230,
              left: 230,
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `${fileSafe(student?.name || "student-result")}-report-card.docx`;
  downloadBlob(blob, filename);
};
